<template>
  <div class="details-screen">
    <!-- Header -->
    <div class="screen-header">
      <button class="screen-header__back" @click="goBack">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 class="screen-header__title">{{ t("problem_details") }}</h1>
    </div>

    <!-- Content -->
    <div class="details-content">
      <!-- Input Preview Section -->
      <section class="details-section">
        <h3 class="details-section__title">{{ t("input") }}</h3>
        <div class="input-preview-card">
          <!-- Text Input -->
          <div v-if="inputType === 'text'" class="input-preview-card__text">
            <p>{{ inputContent }}</p>
          </div>
          <!-- Image Input -->
          <div v-else-if="inputUrl" class="input-preview-card__image">
            <img :src="inputUrl" alt="Problem input" />
          </div>
        </div>
      </section>

      <!-- Status Section -->
      <section class="details-section">
        <h3 class="details-section__title">{{ t("solution_status") }}</h3>

        <!-- Loading -->
        <div v-if="loading" class="status-card status-card--loading">
          <div class="spinner"></div>
          <span>{{ t("loading") }}</span>
        </div>

        <!-- Processing -->
        <div
          v-else-if="isProcessing"
          class="status-card status-card--processing"
        >
          <div class="processing-animation">
            <div class="processing-animation__ring"></div>
            <div class="processing-animation__icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M9 3h6M10 3v6.5L5 19h14l-5-9.5V3" />
              </svg>
            </div>
          </div>
          <h4>{{ t("solving_problem") }}</h4>
          <p>{{ t("wait_or_retry") }}</p>
          <div class="progress-bar">
            <div
              class="progress-bar__fill"
              :style="{ width: `${progressPercent}%` }"
            ></div>
          </div>
          <span class="progress-text">{{ progressPercent }}%</span>
        </div>

        <!-- Completed -->
        <div v-else-if="isCompleted" class="status-card status-card--completed">
          <div class="status-icon status-icon--success">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
          </div>
          <h4>{{ t("solution_ready") }}</h4>
        </div>

        <!-- Failed -->
        <div v-else-if="isFailed" class="status-card status-card--failed">
          <div class="status-icon status-icon--error">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h4>{{ t("solution_failed") }}</h4>
          <p>{{ errorMessage || t("submission_failed") }}</p>
          <button
            class="btn btn--primary"
            @click="handleRetry"
            :disabled="retrying"
          >
            {{ retrying ? t("loading") : t("retry") }}
          </button>
        </div>

        <!-- Timeout -->
        <div v-else-if="isTimeout" class="status-card status-card--timeout">
          <div class="status-icon status-icon--warning">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
          </div>
          <h4>{{ t("taking_longer") }}</h4>
          <p>{{ t("wait_or_retry") }}</p>
          <button class="btn btn--secondary" @click="handleRetry">
            {{ t("retry") }}
          </button>
        </div>
      </section>

      <!-- Solution Section -->
      <section v-if="isCompleted && solution" class="details-section">
        <h3 class="details-section__title">Solution</h3>
        <div class="solution-card">
          <LatexRenderer :content="solutionText" :is-rtl="isRtl" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useMiaStore } from "../stores/miaStore";
import { usePolling } from "../services/pollingService";
import { OperationStatus, PollingConfig } from "../types";
import { useI18n } from "../i18n";
import LatexRenderer from "../components/common/LatexRenderer.vue";

export default {
  name: "ProblemDetailsScreen",
  components: {
    LatexRenderer,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const miaStore = useMiaStore();
    const polling = usePolling();
    const { t, isRtl } = useI18n();

    const problemId = ref(null);
    const solutionId = ref(null);
    const inputType = ref(null);
    const inputContent = ref(null);
    const inputUrl = ref(null);

    const loading = ref(true);
    const status = ref(null);
    const solution = ref(null);
    const errorMessage = ref(null);
    const retrying = ref(false);
    const isTimeout = ref(false);
    const progressPercent = ref(0);

    const isProcessing = computed(
      () =>
        status.value === OperationStatus.PENDING ||
        status.value === OperationStatus.PROCESSING
    );
    const isCompleted = computed(
      () => status.value === OperationStatus.COMPLETED
    );
    const isFailed = computed(() => status.value === OperationStatus.FAILED);

    const formattedSolution = computed(() => {
      if (!solution.value) return "";
      // Convert markdown-like content to HTML
      let content =
        solution.value.content || solution.value.result || solution.value;
      if (typeof content === "object") {
        content = JSON.stringify(content, null, 2);
      }
      // Basic markdown conversion
      return content
        .replace(/\n/g, "<br>")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/g, "<em>$1</em>");
    });

    const solutionText = computed(() => {
      if (!solution.value) return "";

      const data = solution.value;

      // Handle structured solution with steps array
      if (data.steps && Array.isArray(data.steps)) {
        let content = "";
        if (data.title) {
          content += `## ${data.title}\n\n`;
        }
        // Join steps with double newlines
        content += data.steps.join("\n\n");
        return content;
      }

      // Handle simple content
      let content = data.content || data.result || data;
      if (typeof content === "object") {
        // Try to extract meaningful content from object
        if (content.steps) {
          return content.steps.join("\n\n");
        }
        content = JSON.stringify(content, null, 2);
      }
      return content;
    });

    const fetchStatus = async () => {
      if (!problemId.value || !solutionId.value) return;

      const result = await miaStore.getProblemStatus(
        problemId.value,
        solutionId.value
      );

      if (result.success) {
        const data = result.data;
        status.value = data.status;
        solution.value = data.solution || data.result || data;
        errorMessage.value = data.error_message || data.errorMessage;

        // Update progress
        if (data.progress !== undefined) {
          progressPercent.value = Math.round(data.progress * 100);
        } else if (isProcessing.value) {
          // Simulate progress
          progressPercent.value = Math.min(progressPercent.value + 5, 90);
        }
      }
    };

    const startStatusPolling = () => {
      polling.start({
        id: `problem-${problemId.value}-${solutionId.value}`,
        endpoint: `ai/problems/${problemId.value}/solutions/${solutionId.value}/status`,
        onUpdate: (data) => {
          status.value = data.status;
          solution.value = data.solution || data.result || data;
          errorMessage.value = data.error_message || data.errorMessage;

          if (data.progress !== undefined) {
            progressPercent.value = Math.round(data.progress * 100);
          } else if (isProcessing.value) {
            progressPercent.value = Math.min(progressPercent.value + 5, 90);
          }
        },
        onComplete: (data) => {
          status.value = OperationStatus.COMPLETED;
          solution.value = data.solution || data.result || data;
          progressPercent.value = 100;
        },
        onFailed: (data) => {
          status.value = OperationStatus.FAILED;
          errorMessage.value =
            data.error_message || data.errorMessage || "Solution failed";
        },
        onTimeout: () => {
          isTimeout.value = true;
        },
        checkComplete: (data) => data.status === OperationStatus.COMPLETED,
        checkFailed: (data) => data.status === OperationStatus.FAILED,
      });
    };

    const handleRetry = async () => {
      if (retrying.value) return;

      retrying.value = true;
      isTimeout.value = false;
      status.value = OperationStatus.PROCESSING;
      progressPercent.value = 0;

      const result = await miaStore.retryProblem(
        problemId.value,
        solutionId.value
      );

      if (result.success) {
        // Restart polling
        startStatusPolling();
      } else {
        status.value = OperationStatus.FAILED;
        errorMessage.value = result.error || "Retry failed";
      }

      retrying.value = false;
    };

    const goBack = () => {
      router.push("/mia/problems");
    };

    onMounted(async () => {
      // Get params from route
      problemId.value = route.query.problemId;
      solutionId.value = route.query.solutionId;
      inputType.value = route.query.inputType;
      inputContent.value = route.query.content;
      inputUrl.value = route.query.inputUrl;

      if (!problemId.value || !solutionId.value) {
        router.push("/mia/problems");
        return;
      }

      // Initial fetch
      await fetchStatus();
      loading.value = false;

      // Start polling if still processing
      if (isProcessing.value) {
        startStatusPolling();
      }
    });

    onUnmounted(() => {
      polling.cleanup();
    });

    return {
      t,
      problemId,
      solutionId,
      inputType,
      inputContent,
      inputUrl,
      loading,
      status,
      solution,
      errorMessage,
      retrying,
      isTimeout,
      progressPercent,
      isProcessing,
      isCompleted,
      isFailed,
      formattedSolution,
      solutionText,
      isRtl,
      handleRetry,
      goBack,
    };
  },
};
</script>

<style scoped>
.details-screen {
  min-height: 100vh;
  background: #f9fafb;
}

.screen-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
}

.screen-header__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: #374151;
  transition: background 0.2s ease;
}

.screen-header__back:hover {
  background: #f3f4f6;
}

.screen-header__title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.details-content {
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
}

.details-section {
  margin-bottom: 24px;
}

.details-section__title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.input-preview-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.input-preview-card__text {
  padding: 16px;
}

.input-preview-card__text p {
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.input-preview-card__image {
  padding: 16px;
  display: flex;
  justify-content: center;
}

.input-preview-card__image img {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: contain;
}

.status-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 32px;
  text-align: center;
}

.status-card h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 16px 0 8px;
}

.status-card p {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px;
}

.status-card--loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6b7280;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.processing-animation {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
}

.processing-animation__ring {
  position: absolute;
  inset: 0;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

.processing-animation__icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
}

.progress-bar {
  width: 100%;
  max-width: 300px;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  margin: 16px auto 8px;
  overflow: hidden;
}

.progress-bar__fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #6366f1;
}

.status-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.status-icon--success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.status-icon--error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.status-icon--warning {
  background: rgba(249, 115, 22, 0.1);
  color: #f97316;
}

.solution-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 24px;
}

.solution-content {
  font-size: 14px;
  color: #374151;
  line-height: 1.8;
}

.btn {
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn--primary {
  background: #6366f1;
  color: white;
  border: none;
}

.btn--primary:hover:not(:disabled) {
  background: #4f46e5;
}

.btn--primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn--secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn--secondary:hover {
  background: #f3f4f6;
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .details-screen,
[data-theme="dark"] .details-screen,
.dark .details-screen {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .screen-header,
[data-theme="dark"] .screen-header,
.dark .screen-header {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .screen-header__back,
[data-theme="dark"] .screen-header__back,
.dark .screen-header__back {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-header__back:hover,
[data-theme="dark"] .screen-header__back:hover,
.dark .screen-header__back:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .screen-header__title,
[data-theme="dark"] .screen-header__title,
.dark .screen-header__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .details-section__title,
[data-theme="dark"] .details-section__title,
.dark .details-section__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .input-preview-card,
[data-theme="dark"] .input-preview-card,
.dark .input-preview-card {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .input-preview-card__text p,
[data-theme="dark"] .input-preview-card__text p,
.dark .input-preview-card__text p {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .status-card,
[data-theme="dark"] .status-card,
.dark .status-card {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .status-card h4,
[data-theme="dark"] .status-card h4,
.dark .status-card h4 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .status-card p,
[data-theme="dark"] .status-card p,
.dark .status-card p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .status-card--loading,
[data-theme="dark"] .status-card--loading,
.dark .status-card--loading {
  color: #94a3b8;
}

[data-bs-theme="dark"] .spinner,
[data-theme="dark"] .spinner,
.dark .spinner {
  border-color: #475569;
  border-top-color: #00c444;
}

[data-bs-theme="dark"] .processing-animation__ring,
[data-theme="dark"] .processing-animation__ring,
.dark .processing-animation__ring {
  border-color: #475569;
  border-top-color: #00c444;
}

[data-bs-theme="dark"] .processing-animation__icon,
[data-theme="dark"] .processing-animation__icon,
.dark .processing-animation__icon {
  color: #00c444;
}

[data-bs-theme="dark"] .progress-bar,
[data-theme="dark"] .progress-bar,
.dark .progress-bar {
  background: #475569;
}

[data-bs-theme="dark"] .progress-bar__fill,
[data-theme="dark"] .progress-bar__fill,
.dark .progress-bar__fill {
  background: linear-gradient(90deg, #00c444, #00a83b);
}

[data-bs-theme="dark"] .progress-text,
[data-theme="dark"] .progress-text,
.dark .progress-text {
  color: #00c444;
}

[data-bs-theme="dark"] .solution-card,
[data-theme="dark"] .solution-card,
.dark .solution-card {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .solution-content,
[data-theme="dark"] .solution-content,
.dark .solution-content {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .btn--primary,
[data-theme="dark"] .btn--primary,
.dark .btn--primary {
  background: #00c444;
}

[data-bs-theme="dark"] .btn--primary:hover:not(:disabled),
[data-theme="dark"] .btn--primary:hover:not(:disabled),
.dark .btn--primary:hover:not(:disabled) {
  background: #00a83b;
}

[data-bs-theme="dark"] .btn--secondary,
[data-theme="dark"] .btn--secondary,
.dark .btn--secondary {
  background: #334155;
  color: #e2e8f0;
  border-color: #475569;
}

[data-bs-theme="dark"] .btn--secondary:hover,
[data-theme="dark"] .btn--secondary:hover,
.dark .btn--secondary:hover {
  background: #475569;
}
</style>
